import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2, Plus, ChevronUp, ChevronDown, Lock } from 'lucide-react'
import {
  absenceTypesApi,
  type AbsenceTypeFull,
  type AbsenceTypeCreatePayload,
  type AbsenceTypeUpdatePayload,
  type DocDue,
} from '@/services/api/absenceTypes'
import { configApi } from '@/services/api/config'

const DUE_OPTIONS: { value: DocDue; label: string }[] = [
  { value: 'immediate', label: 'Sofort' },
  { value: 'never', label: 'Gar nicht' },
  { value: 'after_days', label: 'Ab N Tagen' },
]

export function AbsenceTypeEditor({ disabled = false }: { disabled?: boolean }) {
  const queryClient = useQueryClient()
  const [newLabel, setNewLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [daysDraft, setDaysDraft] = useState<Record<string, string>>({})

  const { data, isLoading, isError } = useQuery({
    queryKey: ['absence-types-full'],
    queryFn: absenceTypesApi.list,
    staleTime: 30 * 1000,
  })

  const { data: directorCfg } = useQuery({
    queryKey: ['config', 'director'],
    queryFn: () => configApi.getConfig('director'),
    staleTime: 30 * 1000,
  })

  const thresholdDays = Number(directorCfg?.config?.director_duration_threshold_days) || 3

  const types: AbsenceTypeFull[] = useMemo(
    () => (data?.absence_types ?? []) as AbsenceTypeFull[],
    [data],
  )

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['absence-types-full'] })
    queryClient.invalidateQueries({ queryKey: ['absence-types'] })
  }

  const fail = (e: unknown) => {
    const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      ?? (e instanceof Error ? e.message : 'Aktion fehlgeschlagen')
    setError(String(msg))
  }

  const mutationOpts = {
    onSuccess: () => { setError(null); invalidate() },
    onError: fail,
  }

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AbsenceTypeUpdatePayload }) =>
      absenceTypesApi.update(id, payload),
    ...mutationOpts,
  })
  const createMut = useMutation({
    mutationFn: (payload: AbsenceTypeCreatePayload) => absenceTypesApi.create(payload),
    ...mutationOpts,
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => absenceTypesApi.remove(id),
    ...mutationOpts,
  })
  const reorderMut = useMutation({
    mutationFn: (orderedIds: string[]) => absenceTypesApi.reorder(orderedIds),
    ...mutationOpts,
  })

  useEffect(() => { if (isError) setError('Abwesenheitsarten konnten nicht geladen werden.') }, [isError])

  const sorted = [...types].sort((a, b) => a.sort_order - b.sort_order)

  const move = (id: string, dir: -1 | 1) => {
    const idx = sorted.findIndex(t => t.id === id)
    const swapIdx = idx + dir
    if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return
    const next = [...sorted]
    const tmp = next[idx] as AbsenceTypeFull
    next[idx] = next[swapIdx] as AbsenceTypeFull
    next[swapIdx] = tmp
    reorderMut.mutate(next.map(t => t.id))
  }

  const handleAdd = () => {
    const label = newLabel.trim()
    if (!label) return
    createMut.mutate(
      { label, gate_approval_required: false, doc_due: 'never' },
      { onSuccess: () => setNewLabel('') },
    )
  }

  const handleDelete = (t: AbsenceTypeFull) => {
    if (t.is_protected) return
    if (!window.confirm(
      `Abwesenheitsart «${t.label}» löschen?\n\nAlle Anfragen dieser Art fallen auf «Andere» zurück. ` +
      'Bestehende Anfragen zeigen weiterhin den damaligen Namen (Snapshot).'
    )) return
    deleteMut.mutate(t.id)
  }

  const saveLabel = (t: AbsenceTypeFull) => {
    const label = editLabel.trim()
    setEditingId(null)
    if (label && label !== t.label) updateMut.mutate({ id: t.id, payload: { label } })
  }

  const setDue = (t: AbsenceTypeFull, due: DocDue) => {
    if (t.doc_due === due) return
    if (due === 'after_days') {
      const days = t.doc_due_days ?? thresholdDays
      updateMut.mutate({ id: t.id, payload: { doc_due: due, doc_due_days: days } })
      return
    }
    updateMut.mutate({ id: t.id, payload: { doc_due: due, doc_due_days: null } })
  }

  const saveDays = (t: AbsenceTypeFull) => {
    const raw = daysDraft[t.id] ?? String(t.doc_due_days ?? thresholdDays)
    const n = Number(raw)
    if (!Number.isFinite(n) || n < 1 || n > 365) {
      setDaysDraft(d => { const next = { ...d }; delete next[t.id]; return next })
      setError('N muss zwischen 1 und 365 liegen')
      return
    }
    if (n === t.doc_due_days) {
      setDaysDraft(d => { const next = { ...d }; delete next[t.id]; return next })
      return
    }
    updateMut.mutate({ id: t.id, payload: { doc_due: 'after_days', doc_due_days: n } })
  }

  const busy = updateMut.isPending || createMut.isPending || deleteMut.isPending || reorderMut.isPending

  if (isLoading) return <div className="text-sm text-gray-500 py-3">Abwesenheitsarten werden geladen…</div>

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-3 font-medium">Name</th>
              <th className="py-2 pr-3 font-medium w-20">Aktiv</th>
              <th className="py-2 pr-3 font-medium w-16">Reihenfolge</th>
              <th className="py-2 pr-3 font-medium w-40">Gate-Freigabe</th>
              <th className="py-2 pr-3 font-medium">Dokumente</th>
              <th className="py-2 font-medium w-12"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(t => (
              <tr key={t.id} className="border-b border-gray-100 last:border-b-0 align-top">
                <td className="py-2.5 pr-3">
                  {editingId === t.id ? (
                    <input
                      autoFocus
                      value={editLabel}
                      onChange={e => setEditLabel(e.target.value)}
                      onBlur={() => saveLabel(t)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveLabel(t)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      maxLength={100}
                      className="w-full px-2 py-1 border border-indigo-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                  ) : (
                    <button
                      type="button"
                      disabled={disabled || busy || t.is_protected}
                      onClick={() => { setEditingId(t.id); setEditLabel(t.label) }}
                      title={t.is_protected
                        ? '«Andere» ist geschützt und kann nicht umbenannt werden'
                        : disabled ? 'Keine Bearbeitungsrechte' : 'Name ändern'}
                      className={`font-medium text-left ${disabled || t.is_protected
                        ? 'text-gray-800 cursor-not-allowed'
                        : 'text-gray-800 hover:text-indigo-600 underline decoration-dotted underline-offset-4'}`}
                    >
                      {t.label}
                      {t.is_protected && <Lock className="inline ml-1.5 w-3 h-3 text-gray-400" />}
                    </button>
                  )}
                </td>
                <td className="py-2.5 pr-3">
                  <button
                    onClick={() => updateMut.mutate({ id: t.id, payload: { is_active: !t.is_active } })}
                    disabled={disabled || busy || t.is_protected}
                    title={t.is_protected ? '«Andere» ist immer aktiv' : ''}
                    role="switch" aria-checked={t.is_active}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      t.is_active ? 'bg-indigo-600' : 'bg-gray-300'
                    } ${disabled || busy || t.is_protected ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      t.is_active ? 'translate-x-[18px]' : 'translate-x-1'}`} />
                  </button>
                </td>
                <td className="py-2.5 pr-3">
                  <div className="flex flex-col gap-0.5">
                    <button type="button" onClick={() => move(t.id, -1)}
                      disabled={disabled || busy || sorted[0]?.id === t.id}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                      title="Nach oben">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => move(t.id, 1)}
                      disabled={disabled || busy || sorted[sorted.length - 1]?.id === t.id}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                      title="Nach unten">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                <td className="py-2.5 pr-3">
                  <button
                    onClick={() => updateMut.mutate({ id: t.id, payload: { gate_approval_required: !t.gate_approval_required } })}
                    disabled={disabled || busy}
                    role="switch" aria-checked={t.gate_approval_required}
                    title="Bei dieser Abwesenheits-Art ist die Direktor-Freigabe nötig"
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      t.gate_approval_required ? 'bg-amber-500' : 'bg-gray-300'
                    } ${disabled || busy ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      t.gate_approval_required ? 'translate-x-[18px]' : 'translate-x-1'}`} />
                  </button>
                </td>
                <td className="py-2.5 pr-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {DUE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={disabled || busy}
                        onClick={() => setDue(t, opt.value)}
                        className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                          t.doc_due === opt.value
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium'
                            : 'bg-white border-gray-300 text-gray-500'
                        } ${disabled || busy ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                    {t.doc_due === 'after_days' && (
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={daysDraft[t.id] ?? String(t.doc_due_days ?? thresholdDays)}
                        disabled={disabled || busy}
                        onChange={e => setDaysDraft(d => ({ ...d, [t.id]: e.target.value }))}
                        onBlur={() => saveDays(t)}
                        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-md"
                        title="Pflicht ab N distinkten Schultagen"
                      />
                    )}
                  </div>
                </td>
                <td className="py-2.5">
                  <button
                    type="button"
                    onClick={() => handleDelete(t)}
                    disabled={disabled || busy || t.is_protected}
                    title={t.is_protected ? '«Andere» kann nicht gelöscht werden (Fallback-Ziel)' : 'Löschen'}
                    className={`p-1.5 rounded ${disabled || busy || t.is_protected
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-red-500 hover:bg-red-50'}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <input
          type="text"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd() }}
          placeholder="z.B. Militärdienst, Sportwoche …"
          maxLength={100}
          disabled={disabled || busy}
          className="flex-1 max-w-xs px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled || busy || !newLabel.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" /> Art hinzufügen
        </button>
      </div>

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  )
}
